<template>
  <div class="merge-rule-management">
    <div class="page-header">
      <h2>🔧 智能归并规则管理</h2>
      <p>配置智能归并规则，自动识别和归并相关的票据与银证，简化财务处理流程</p>
    </div>

    <!-- 快速模板选择 -->
    <div class="template-section margin-bottom">
      <h3>📋 常用规则模板</h3>
      <div class="template-cards">
        <div v-for="template in ruleTemplates" :key="template.id"
             class="template-card" @click="useTemplate(template)">
          <div class="template-icon">{{ template.icon }}</div>
          <div class="template-content">
            <h4>{{ template.name }}</h4>
            <p>{{ template.description }}</p>
            <div class="template-tags">
              <span v-for="tag in template.tags" :key="tag" class="template-tag">{{ tag }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="toolbar margin-bottom">
      <Button color="primary" @click="showCreateRuleDialog">
        <i class="h-icon-plus"></i> 自定义规则
      </Button>
      <Button @click="loadMergeRules" icon="h-icon-refresh">刷新</Button>
      <Button @click="showBatchImport" icon="h-icon-upload">批量导入</Button>
      <Button @click="exportRules" icon="h-icon-download">导出规则</Button>
    </div>

    <!-- 规则分类标签 -->
    <div class="rule-categories margin-bottom">
      <Button v-for="category in ruleCategories" :key="category.key"
              :class="{'h-btn-primary': selectedCategory === category.key}"
              @click="selectedCategory = category.key">
        {{ category.name }} ({{ getCategoryCount(category.key) }})
      </Button>
    </div>

    <!-- 规则列表 -->
    <div class="rules-container">
      <div v-if="filteredRules.length === 0" class="empty-state">
        <div class="empty-icon">📝</div>
        <h3>暂无归并规则</h3>
        <p>点击上方模板快速创建，或自定义规则</p>
      </div>

      <div v-for="rule in filteredRules" :key="rule.ruleId" class="rule-card">
        <div class="rule-header">
          <div class="rule-title">
            <h4>{{ rule.ruleName }}</h4>
            <span class="rule-category">{{ getRuleCategoryName(rule.category) }}</span>
          </div>
          <div class="rule-status">
            <span class="status-badge" :class="rule.isActive ? 'active' : 'inactive'">
              {{ rule.isActive ? '✅ 启用' : '⏸️ 禁用' }}
            </span>
            <span class="rule-type" :class="rule.applicableEntity.toLowerCase()">
              {{ getEntityTypeText(rule.applicableEntity) }}
            </span>
          </div>
        </div>

        <div class="rule-description">
          {{ rule.ruleDescription }}
        </div>

        <div class="rule-conditions" v-if="rule.parsedLogic">
          <div class="condition-summary">
            <span v-for="condition in rule.conditionSummary" :key="condition" class="condition-tag">
              {{ condition }}
            </span>
          </div>
        </div>

        <div class="rule-footer">
          <div class="rule-meta">
            <span>创建时间：{{ formatDate(rule.createdAt) }}</span>
            <span v-if="rule.lastUsed">最后使用：{{ formatDate(rule.lastUsed) }}</span>
          </div>
          <div class="rule-actions">
            <Button size="small" @click="testRule(rule)" icon="h-icon-play">测试</Button>
            <Button size="small" @click="viewRuleDetail(rule)" icon="h-icon-eye">详情</Button>
            <Button size="small" color="primary" @click="editRule(rule)" icon="h-icon-edit">编辑</Button>
            <Button size="small" :color="rule.isActive ? 'yellow' : 'green'"
                    @click="toggleRule(rule)" :icon="rule.isActive ? 'h-icon-pause' : 'h-icon-play'">
              {{ rule.isActive ? '禁用' : '启用' }}
            </Button>
            <Button size="small" color="red" @click="deleteRule(rule.ruleId)" icon="h-icon-delete">删除</Button>
          </div>
        </div>
      </div>
    </div>

    <!-- 创建/编辑规则对话框 -->
    <Modal v-model="ruleDialogVisible" :title="dialogTitle" width="900px" class="rule-dialog">
      <div class="rule-form">
        <!-- 基本信息 -->
        <div class="form-section">
          <h3>📝 基本信息</h3>
          <div class="form-row">
            <label>规则名称：</label>
            <input v-model="ruleForm.ruleName" placeholder="请输入规则名称"
                   class="form-input" style="width: 300px;" />
          </div>

          <div class="form-row">
            <label>规则描述：</label>
            <textarea v-model="ruleForm.ruleDescription" placeholder="请输入规则描述"
                      class="form-textarea" rows="3"></textarea>
          </div>

          <div class="form-row">
            <label>适用类型：</label>
            <Select v-model="ruleForm.applicableEntity" :datas="entityTypeOptions"
                    style="width: 200px;" />
          </div>

          <div class="form-row">
            <label>规则分类：</label>
            <Select v-model="ruleForm.category" :datas="ruleCategoryOptions"
                    style="width: 200px;" />
          </div>

          <div class="form-row">
            <label>优先级：</label>
            <Select v-model="ruleForm.priority" :datas="priorityOptions"
                    style="width: 150px;" />
          </div>
        </div>

        <!-- 匹配条件 -->
        <div class="form-section">
          <h3>🎯 匹配条件</h3>

          <!-- 时间条件 -->
          <div class="condition-group">
            <h4>⏰ 时间条件</h4>
            <div class="form-row">
              <label>时间范围：</label>
              <Select v-model="ruleForm.timeRange" :datas="timeRangeOptions"
                      style="width: 200px;" />
            </div>
            <div class="form-row" v-if="ruleForm.timeRange === 'CUSTOM'">
              <label>自定义天数：</label>
              <input v-model.number="ruleForm.customDays" type="number" min="1" max="365"
                     class="form-input" style="width: 100px;" />
              <span>天</span>
            </div>
          </div>

          <!-- 金额条件 -->
          <div class="condition-group">
            <h4>💰 金额条件</h4>
            <div class="form-row">
              <Checkbox v-model="ruleForm.enableAmountFilter">启用金额筛选</Checkbox>
            </div>
            <div v-if="ruleForm.enableAmountFilter">
              <div class="form-row">
                <label>金额范围：</label>
                <input v-model.number="ruleForm.minAmount" type="number" min="0" step="0.01"
                       class="form-input" style="width: 120px;" placeholder="最小金额" />
                <span style="margin: 0 10px;">至</span>
                <input v-model.number="ruleForm.maxAmount" type="number" min="0" step="0.01"
                       class="form-input" style="width: 120px;" placeholder="最大金额" />
              </div>
              <div class="form-row">
                <label>金额容差：</label>
                <input v-model.number="ruleForm.amountTolerance" type="number" min="0" step="0.01"
                       class="form-input" style="width: 120px;" />
                <span>元 或</span>
                <input v-model.number="ruleForm.tolerancePercent" type="number" min="0" max="100" step="0.1"
                       class="form-input" style="width: 80px;" />
                <span>%</span>
              </div>
            </div>
          </div>

          <!-- 关键词条件 -->
          <div class="condition-group">
            <h4>🔍 关键词条件</h4>
            <div class="form-row">
              <Checkbox v-model="ruleForm.enableKeywordFilter">启用关键词匹配</Checkbox>
            </div>
            <div v-if="ruleForm.enableKeywordFilter">
              <div class="form-row">
                <label>包含关键词：</label>
                <input v-model="ruleForm.includeKeywords" placeholder="多个关键词用逗号分隔"
                       class="form-input" style="width: 300px;" />
              </div>
              <div class="form-row">
                <label>排除关键词：</label>
                <input v-model="ruleForm.excludeKeywords" placeholder="多个关键词用逗号分隔"
                       class="form-input" style="width: 300px;" />
              </div>
            </div>
          </div>

          <!-- 人员/部门条件 -->
          <div class="condition-group">
            <h4>👥 人员条件</h4>
            <div class="form-row">
              <Checkbox v-model="ruleForm.enablePersonFilter">启用人员筛选</Checkbox>
            </div>
            <div v-if="ruleForm.enablePersonFilter">
              <div class="form-row">
                <label>相同提交人：</label>
                <Checkbox v-model="ruleForm.sameSubmitter">要求相同提交人</Checkbox>
              </div>
              <div class="form-row">
                <label>相同部门：</label>
                <Checkbox v-model="ruleForm.sameDepartment">要求相同部门</Checkbox>
              </div>
            </div>
          </div>

          <!-- 交易方条件 -->
          <div class="condition-group">
            <h4>🏢 交易方条件</h4>
            <div class="form-row">
              <Checkbox v-model="ruleForm.enableCounterpartyFilter">启用交易方筛选</Checkbox>
            </div>
            <div v-if="ruleForm.enableCounterpartyFilter">
              <div class="form-row">
                <label>相同交易方：</label>
                <Checkbox v-model="ruleForm.sameCounterparty">要求相同交易方</Checkbox>
              </div>
              <div class="form-row">
                <label>指定交易方：</label>
                <input v-model="ruleForm.specificCounterparty" placeholder="指定交易方名称"
                       class="form-input" style="width: 250px;" />
              </div>
            </div>
          </div>
        </div>

        <!-- 高级设置 -->
        <div class="form-section">
          <h3>⚙️ 高级设置</h3>
          <div class="form-row">
            <label>自动执行：</label>
            <Checkbox v-model="ruleForm.autoExecute">新数据自动应用此规则</Checkbox>
          </div>
          <div class="form-row">
            <label>启用状态：</label>
            <Checkbox v-model="ruleForm.isActive">启用此规则</Checkbox>
          </div>
        </div>
      </div>

      <div slot="footer">
        <Button @click="cancelRuleDialog">取消</Button>
        <Button @click="testRuleConfig" color="yellow">测试规则</Button>
        <Button @click="saveRule" color="primary">保存规则</Button>
      </div>
    </Modal>

    <!-- 规则详情对话框 -->
    <Modal v-model="detailDialogVisible" title="规则详情" width="800px">
      <div v-if="selectedRule" class="rule-detail">
        <div class="detail-section">
          <h3>📋 基本信息</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <label>规则名称：</label>
              <span>{{ selectedRule.ruleName }}</span>
            </div>
            <div class="detail-item">
              <label>规则描述：</label>
              <span>{{ selectedRule.ruleDescription }}</span>
            </div>
            <div class="detail-item">
              <label>适用类型：</label>
              <span class="rule-type" :class="selectedRule.applicableEntity.toLowerCase()">
                {{ getEntityTypeText(selectedRule.applicableEntity) }}
              </span>
            </div>
            <div class="detail-item">
              <label>规则分类：</label>
              <span>{{ getRuleCategoryName(selectedRule.category) }}</span>
            </div>
            <div class="detail-item">
              <label>状态：</label>
              <span class="status-badge" :class="selectedRule.isActive ? 'active' : 'inactive'">
                {{ selectedRule.isActive ? '✅ 启用' : '⏸️ 禁用' }}
              </span>
            </div>
            <div class="detail-item">
              <label>创建时间：</label>
              <span>{{ formatDate(selectedRule.createdAt) }}</span>
            </div>
          </div>
        </div>

        <div class="detail-section" v-if="selectedRule.parsedLogic">
          <h3>🎯 匹配条件</h3>
          <div class="conditions-display">
            <div v-if="selectedRule.parsedLogic.timeRange" class="condition-item">
              <strong>⏰ 时间条件：</strong>
              <span>{{ getTimeRangeText(selectedRule.parsedLogic.timeRange) }}</span>
            </div>
            <div v-if="selectedRule.parsedLogic.enableAmountFilter" class="condition-item">
              <strong>💰 金额条件：</strong>
              <span>{{ formatAmountCondition(selectedRule.parsedLogic) }}</span>
            </div>
            <div v-if="selectedRule.parsedLogic.enableKeywordFilter" class="condition-item">
              <strong>🔍 关键词条件：</strong>
              <span>{{ formatKeywordCondition(selectedRule.parsedLogic) }}</span>
            </div>
            <div v-if="selectedRule.parsedLogic.enablePersonFilter" class="condition-item">
              <strong>👥 人员条件：</strong>
              <span>{{ formatPersonCondition(selectedRule.parsedLogic) }}</span>
            </div>
            <div v-if="selectedRule.parsedLogic.enableCounterpartyFilter" class="condition-item">
              <strong>🏢 交易方条件：</strong>
              <span>{{ formatCounterpartyCondition(selectedRule.parsedLogic) }}</span>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <h3>📊 使用统计</h3>
          <div class="stats-grid">
            <div class="stat-item">
              <div class="stat-number">{{ selectedRule.usageCount || 0 }}</div>
              <div class="stat-label">使用次数</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">{{ selectedRule.successCount || 0 }}</div>
              <div class="stat-label">成功归并</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">{{ selectedRule.lastUsed ? formatDate(selectedRule.lastUsed) : '未使用' }}</div>
              <div class="stat-label">最后使用</div>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <h3>🔧 原始配置</h3>
          <pre class="rule-logic-display">{{ formatRuleLogic(selectedRule.ruleLogic) }}</pre>
        </div>
      </div>
    </Modal>

    <!-- 规则测试对话框 -->
    <Modal v-model="testDialogVisible" title="规则测试" width="900px">
      <div class="test-container">
        <div class="test-header">
          <h3>🧪 测试规则：{{ testingRule && testingRule.ruleName }}</h3>
          <p>预览此规则在当前数据上的归并效果</p>
        </div>

        <div class="test-controls">
          <Button @click="runRuleTest" color="primary" :loading="testLoading">
            <i class="h-icon-play"></i> 运行测试
          </Button>
          <Button @click="clearTestResults">清空结果</Button>
        </div>

        <div v-if="testResults" class="test-results">
          <div class="result-summary">
            <div class="summary-item">
              <span class="summary-number">{{ testResults.totalItems || 0 }}</span>
              <span class="summary-label">待处理项目</span>
            </div>
            <div class="summary-item">
              <span class="summary-number">{{ testResults.matchedGroups || 0 }}</span>
              <span class="summary-label">匹配组数</span>
            </div>
            <div class="summary-item">
              <span class="summary-number">{{ testResults.mergedItems || 0 }}</span>
              <span class="summary-label">可归并项目</span>
            </div>
          </div>

          <div v-if="testResults.groups && testResults.groups.length > 0" class="test-groups">
            <h4>预览归并结果：</h4>
            <div v-for="(group, index) in testResults.groups" :key="index" class="test-group">
              <div class="group-header">
                <strong>归并组 {{ index + 1 }}</strong>
                <span class="group-count">{{ group.items.length }} 项</span>
              </div>
              <div class="group-items">
                <div v-for="item in group.items" :key="item.id" class="group-item">
                  <span class="item-name">{{ item.summary || item.receiptTitle }}</span>
                  <span class="item-amount">¥{{ formatAmount(item.amount) }}</span>
                  <span class="item-date">{{ formatDate(item.billDate || item.transactionDate) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>

    <!-- 批量导入对话框 -->
    <Modal v-model="importDialogVisible" title="批量导入规则" width="600px">
      <div class="import-container">
        <div class="import-tips">
          <h4>📥 导入说明</h4>
          <ul>
            <li>支持JSON格式的规则配置文件</li>
            <li>可以从其他账套导出的规则文件</li>
            <li>导入前会进行格式验证</li>
          </ul>
        </div>

        <div class="file-upload">
          <input type="file" ref="fileInput" @change="handleFileSelect" accept=".json" style="display: none;">
          <Button @click="selectFile" icon="h-icon-upload">选择文件</Button>
          <span v-if="selectedFile">{{ selectedFile.name }}</span>
        </div>

        <div v-if="importPreview" class="import-preview">
          <h4>预览导入规则：</h4>
          <div v-for="rule in importPreview" :key="rule.ruleName" class="preview-rule">
            <span>{{ rule.ruleName }}</span>
            <span class="rule-category">{{ rule.category }}</span>
          </div>
        </div>
      </div>

      <div slot="footer">
        <Button @click="importDialogVisible = false">取消</Button>
        <Button @click="executeImport" color="primary" :disabled="!selectedFile">导入</Button>
      </div>
    </Modal>
  </div>
</template>

<script>
export default {
  name: 'MergeRuleManagement',
  data() {
    return {
      mergeRules: [],
      rulesLoading: false,
      ruleDialogVisible: false,
      detailDialogVisible: false,
      testDialogVisible: false,
      importDialogVisible: false,
      isEditing: false,
      selectedRule: null,
      testingRule: null,
      testResults: null,
      testLoading: false,
      selectedCategory: 'ALL',
      selectedFile: null,
      importPreview: null,

      ruleForm: {
        ruleId: '',
        ruleName: '',
        ruleDescription: '',
        applicableEntity: 'BOTH',
        category: 'BUSINESS',
        priority: 'MEDIUM',

        // 时间条件
        timeRange: 'WEEK',
        customDays: 7,

        // 金额条件
        enableAmountFilter: false,
        minAmount: 0,
        maxAmount: 10000,
        amountTolerance: 0,
        tolerancePercent: 5,

        // 关键词条件
        enableKeywordFilter: false,
        includeKeywords: '',
        excludeKeywords: '',

        // 人员条件
        enablePersonFilter: false,
        sameSubmitter: false,
        sameDepartment: false,

        // 交易方条件
        enableCounterpartyFilter: false,
        sameCounterparty: false,
        specificCounterparty: '',

        // 高级设置
        autoExecute: false,
        isActive: true
      },

      // 规则模板
      ruleTemplates: [
        {
          id: 'business_trip',
          name: '出差费用归并',
          icon: '✈️',
          description: '自动归并员工出差相关的交通费、住宿费、餐费等',
          tags: ['出差', '交通', '住宿', '餐费'],
          config: {
            ruleName: '出差费用自动归并',
            ruleDescription: '根据时间范围和费用类型自动归并出差相关费用',
            category: 'BUSINESS',
            timeRange: 'MONTH',
            enableKeywordFilter: true,
            includeKeywords: '出差,差旅,交通费,住宿费,餐费,机票,火车票,酒店',
            enablePersonFilter: true,
            sameSubmitter: true
          }
        },
        {
          id: 'meeting_event',
          name: '会议活动费用归并',
          icon: '🏢',
          description: '归并会议或活动相关的场地、交通、礼品等费用',
          tags: ['会议', '活动', '场地', '礼品'],
          config: {
            ruleName: '会议活动费用归并',
            ruleDescription: '按活动项目归并相关费用支出',
            category: 'BUSINESS',
            timeRange: 'WEEK',
            enableKeywordFilter: true,
            includeKeywords: '会议,活动,场地,租赁,礼品,茶歇,会务',
            enablePersonFilter: true,
            sameDepartment: true
          }
        },
        {
          id: 'supplier_payment',
          name: '供应商付款归并',
          icon: '🏭',
          description: '按供应商和时间范围归并付款记录',
          tags: ['供应商', '付款', '采购'],
          config: {
            ruleName: '供应商付款归并',
            ruleDescription: '按供应商分组归并付款记录',
            category: 'SUPPLIER',
            timeRange: 'MONTH',
            enableCounterpartyFilter: true,
            sameCounterparty: true,
            enableAmountFilter: true,
            minAmount: 1000
          }
        },
        {
          id: 'office_expense',
          name: '办公费用归并',
          icon: '🏢',
          description: '归并日常办公用品、设备等费用',
          tags: ['办公', '用品', '设备'],
          config: {
            ruleName: '办公费用归并',
            ruleDescription: '按部门和费用类型归并办公支出',
            category: 'OFFICE',
            timeRange: 'MONTH',
            enableKeywordFilter: true,
            includeKeywords: '办公用品,文具,设备,耗材,维修',
            enablePersonFilter: true,
            sameDepartment: true
          }
        },
        {
          id: 'utility_bill',
          name: '公用事业费归并',
          icon: '⚡',
          description: '归并水电费、网络费等定期缴费',
          tags: ['水电', '网络', '定期'],
          config: {
            ruleName: '公用事业费归并',
            ruleDescription: '按费用类型归并定期缴费项目',
            category: 'UTILITY',
            timeRange: 'MONTH',
            enableKeywordFilter: true,
            includeKeywords: '水费,电费,网络费,电话费,物业费',
            enableAmountFilter: true,
            tolerancePercent: 10
          }
        },
        {
          id: 'project_expense',
          name: '项目费用归并',
          icon: '📊',
          description: '按项目编号或名称归并相关费用',
          tags: ['项目', '研发', '专项'],
          config: {
            ruleName: '项目费用归并',
            ruleDescription: '按项目分组归并相关支出',
            category: 'PROJECT',
            timeRange: 'QUARTER',
            enableKeywordFilter: true,
            includeKeywords: '项目,研发,开发,测试',
            enablePersonFilter: true,
            sameDepartment: true
          }
        }
      ],

      entityTypeOptions: [
        { key: 'DOCUMENT', title: '票据' },
        { key: 'RECEIPT', title: '银证' },
        { key: 'BOTH', title: '通用' }
      ],

      ruleCategoryOptions: [
        { key: 'BUSINESS', title: '业务费用' },
        { key: 'OFFICE', title: '办公费用' },
        { key: 'SUPPLIER', title: '供应商' },
        { key: 'UTILITY', title: '公用事业' },
        { key: 'PROJECT', title: '项目费用' },
        { key: 'CUSTOM', title: '自定义' }
      ],

      ruleCategories: [
        { key: 'ALL', name: '全部' },
        { key: 'BUSINESS', name: '业务费用' },
        { key: 'OFFICE', name: '办公费用' },
        { key: 'SUPPLIER', name: '供应商' },
        { key: 'UTILITY', name: '公用事业' },
        { key: 'PROJECT', name: '项目费用' },
        { key: 'CUSTOM', name: '自定义' }
      ],

      priorityOptions: [
        { key: 'HIGH', title: '高优先级' },
        { key: 'MEDIUM', title: '中优先级' },
        { key: 'LOW', title: '低优先级' }
      ],

      timeRangeOptions: [
        { key: 'DAY', title: '同一天' },
        { key: 'WEEK', title: '一周内' },
        { key: 'MONTH', title: '一个月内' },
        { key: 'QUARTER', title: '一个季度内' },
        { key: 'CUSTOM', title: '自定义' }
      ]
    }
  },

  computed: {
    dialogTitle() {
      return this.isEditing ? '编辑规则' : '创建规则'
    },

    filteredRules() {
      if (this.selectedCategory === 'ALL') {
        return this.mergeRules
      }
      return this.mergeRules.filter(rule => rule.category === this.selectedCategory)
    }
  },
  
  mounted() {
    this.loadMergeRules()
  },
  
  methods: {
    // 加载归并规则
    async loadMergeRules() {
      this.rulesLoading = true
      try {
        const response = await fetch('/api/merge-rules/list', {
          credentials: 'include'
        })
        const result = await response.json()
        if (result.success) {
          this.mergeRules = (result.data || []).map(rule => {
            // 解析规则逻辑
            try {
              rule.parsedLogic = JSON.parse(rule.ruleLogic || '{}')
              rule.category = rule.parsedLogic.category || 'CUSTOM'
              rule.conditionSummary = this.buildConditionSummary(rule.parsedLogic)
            } catch (e) {
              rule.parsedLogic = {}
              rule.category = 'CUSTOM'
              rule.conditionSummary = []
            }
            return rule
          })
          console.log('加载归并规则成功:', this.mergeRules.length)
        } else {
          this.$Message.error('加载归并规则失败: ' + result.msg)
        }
      } catch (error) {
        this.$Message.error('加载归并规则失败: ' + error.message)
        console.error('加载归并规则失败:', error)
      } finally {
        this.rulesLoading = false
      }
    },

    // 构建条件摘要
    buildConditionSummary(logic) {
      const summary = []
      if (logic.timeRange) {
        summary.push(this.getTimeRangeText(logic.timeRange))
      }
      if (logic.enableAmountFilter && (logic.minAmount || logic.maxAmount)) {
        summary.push(`金额: ${logic.minAmount || 0}-${logic.maxAmount || '∞'}`)
      }
      if (logic.enableKeywordFilter && logic.includeKeywords) {
        summary.push(`关键词: ${logic.includeKeywords.split(',').slice(0, 2).join(', ')}${logic.includeKeywords.split(',').length > 2 ? '...' : ''}`)
      }
      if (logic.enablePersonFilter) {
        if (logic.sameSubmitter) summary.push('相同提交人')
        if (logic.sameDepartment) summary.push('相同部门')
      }
      if (logic.enableCounterpartyFilter && logic.sameCounterparty) {
        summary.push('相同交易方')
      }
      return summary
    },

    // 使用模板
    useTemplate(template) {
      this.isEditing = false
      this.ruleForm = { ...this.getDefaultRuleForm(), ...template.config }
      this.ruleDialogVisible = true
    },

    // 获取默认表单
    getDefaultRuleForm() {
      return {
        ruleId: '',
        ruleName: '',
        ruleDescription: '',
        applicableEntity: 'BOTH',
        category: 'BUSINESS',
        priority: 'MEDIUM',
        timeRange: 'WEEK',
        customDays: 7,
        enableAmountFilter: false,
        minAmount: 0,
        maxAmount: 10000,
        amountTolerance: 0,
        tolerancePercent: 5,
        enableKeywordFilter: false,
        includeKeywords: '',
        excludeKeywords: '',
        enablePersonFilter: false,
        sameSubmitter: false,
        sameDepartment: false,
        enableCounterpartyFilter: false,
        sameCounterparty: false,
        specificCounterparty: '',
        autoExecute: false,
        isActive: true
      }
    },

    // 获取分类数量
    getCategoryCount(category) {
      if (category === 'ALL') {
        return this.mergeRules.length
      }
      return this.mergeRules.filter(rule => rule.category === category).length
    },

    // 获取分类名称
    getRuleCategoryName(category) {
      const categoryMap = {
        'BUSINESS': '业务费用',
        'OFFICE': '办公费用',
        'SUPPLIER': '供应商',
        'UTILITY': '公用事业',
        'PROJECT': '项目费用',
        'CUSTOM': '自定义'
      }
      return categoryMap[category] || '自定义'
    },

    // 获取时间范围文本
    getTimeRangeText(timeRange) {
      const timeRangeMap = {
        'DAY': '同一天',
        'WEEK': '一周内',
        'MONTH': '一个月内',
        'QUARTER': '一个季度内',
        'CUSTOM': '自定义'
      }
      return timeRangeMap[timeRange] || timeRange
    },

    // 显示创建规则对话框
    showCreateRuleDialog() {
      this.isEditing = false
      this.ruleForm = this.getDefaultRuleForm()
      this.ruleDialogVisible = true
    },

    // 测试规则
    testRule(rule) {
      this.testingRule = rule
      this.testResults = null
      this.testDialogVisible = true
    },

    // 运行规则测试
    async runRuleTest() {
      if (!this.testingRule) return

      this.testLoading = true
      try {
        const result = await Ajax.postJson('/merge-engine/test-rule', {
          ruleId: this.testingRule.ruleId,
          ruleLogic: this.testingRule.ruleLogic,
          applicableEntity: this.testingRule.applicableEntity
        })

        if (result.success) {
          this.testResults = result.data
          this.$Message.success('规则测试完成')
        } else {
          this.$Message.error('规则测试失败: ' + result.msg)
        }
      } catch (error) {
        this.$Message.error('规则测试失败: ' + (error.msg || error.message))
      } finally {
        this.testLoading = false
      }
    },

    // 清空测试结果
    clearTestResults() {
      this.testResults = null
    },

    // 测试规则配置
    async testRuleConfig() {
      try {
        const ruleLogic = this.buildRuleLogic()
        const result = await Ajax.postJson('/merge-engine/test-rule', {
          ruleLogic: ruleLogic,
          applicableEntity: this.ruleForm.applicableEntity
        })

        if (result.success) {
          const data = result.data
          this.$Message.success(`测试完成：找到 ${data.matchedGroups || 0} 个归并组，共 ${data.mergedItems || 0} 个项目可归并`)
        } else {
          this.$Message.error('规则测试失败: ' + result.msg)
        }
      } catch (error) {
        this.$Message.error('规则测试失败: ' + (error.msg || error.message))
      }
    },

    // 显示批量导入
    showBatchImport() {
      this.selectedFile = null
      this.importPreview = null
      this.importDialogVisible = true
    },

    // 选择文件
    selectFile() {
      this.$refs.fileInput.click()
    },

    // 处理文件选择
    handleFileSelect(event) {
      const file = event.target.files[0]
      if (!file) return

      this.selectedFile = file

      // 读取文件内容预览
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const rules = JSON.parse(e.target.result)
          this.importPreview = Array.isArray(rules) ? rules : [rules]
        } catch (error) {
          this.$Message.error('文件格式错误，请选择有效的JSON文件')
          this.selectedFile = null
        }
      }
      reader.readAsText(file)
    },

    // 执行导入
    async executeImport() {
      if (!this.importPreview) return

      try {
        for (const rule of this.importPreview) {
          await this.createSingleRule(rule)
        }
        this.$Message.success(`成功导入 ${this.importPreview.length} 个规则`)
        this.importDialogVisible = false
        this.loadMergeRules()
      } catch (error) {
        this.$Message.error('导入失败: ' + error.message)
      }
    },

    // 导出规则
    exportRules() {
      const exportData = this.mergeRules.map(rule => ({
        ruleName: rule.ruleName,
        ruleDescription: rule.ruleDescription,
        applicableEntity: rule.applicableEntity,
        category: rule.category,
        ruleLogic: rule.ruleLogic,
        isActive: rule.isActive
      }))

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `merge-rules-${new Date().toISOString().split('T')[0]}.json`
      a.click()
      URL.revokeObjectURL(url)

      this.$Message.success('规则导出成功')
    },

    // 编辑规则
    editRule(rule) {
      this.isEditing = true
      this.ruleForm = { ...rule }
      this.parseRuleLogic(rule.ruleLogic)
      this.ruleDialogVisible = true
    },

    // 查看规则详情
    viewRuleDetail(rule) {
      this.selectedRule = rule
      this.detailDialogVisible = true
    },

    // 切换规则状态
    async toggleRule(rule) {
      try {
        const response = await fetch(`/api/merge-rules/${rule.ruleId}/toggle-status`, {
          method: 'POST',
          credentials: 'include'
        })
        const result = await response.json()
        if (result.success) {
          this.$Message.success(`规则已${rule.isActive ? '禁用' : '启用'}`)
          rule.isActive = !rule.isActive
        } else {
          this.$Message.error('切换规则状态失败: ' + result.msg)
        }
      } catch (error) {
        this.$Message.error('切换规则状态失败: ' + error.message)
      }
    },
    
    // 删除规则
    async deleteRule(ruleId) {
      try {
        await this.$confirm('确定要删除这个归并规则吗？删除后无法恢复。', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        const response = await fetch(`/api/merge-rules/${ruleId}/delete`, {
          method: 'DELETE',
          credentials: 'include'
        })
        const result = await response.json()
        if (result.success) {
          this.$Message.success('删除规则成功')
          this.loadMergeRules()
        } else {
          this.$Message.error('删除规则失败: ' + result.msg)
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$Message.error('删除规则失败: ' + error.message)
        }
      }
    },

    // 保存规则
    async saveRule() {
      try {
        if (!this.ruleForm.ruleName.trim()) {
          this.$Message.error('请输入规则名称')
          return
        }
        if (!this.ruleForm.ruleDescription.trim()) {
          this.$Message.error('请输入规则描述')
          return
        }

        const ruleData = {
          ruleName: this.ruleForm.ruleName,
          ruleDescription: this.ruleForm.ruleDescription,
          applicableEntity: this.ruleForm.applicableEntity,
          ruleLogic: this.buildRuleLogic(),
          isActive: this.ruleForm.isActive
        }

        await this.createSingleRule(ruleData)
        this.$Message.success(this.isEditing ? '更新规则成功' : '创建规则成功')
        this.ruleDialogVisible = false
        this.loadMergeRules()
      } catch (error) {
        this.$Message.error('保存规则失败: ' + error.message)
      }
    },

    // 创建单个规则
    async createSingleRule(ruleData) {
      const url = this.isEditing
        ? '/api/merge-rules/update'
        : '/api/merge-rules/create'

      if (this.isEditing) {
        ruleData.ruleId = this.ruleForm.ruleId
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(ruleData)
      })

      const result = await response.json()
      if (!result.success) {
        throw new Error(result.msg || '保存失败')
      }
      return result
    },

    // 取消对话框
    cancelRuleDialog() {
      this.ruleDialogVisible = false
    },
    
    // 重置表单
    resetRuleForm() {
      this.ruleForm = {
        ruleId: '',
        ruleName: '',
        ruleDescription: '',
        applicableEntity: 'BOTH',
        ruleType: '',
        dateTolerance: 0,
        amountTolerance: 0,
        minAmount: 0,
        maxAmount: 10000,
        tolerancePercent: 5,
        windowDays: 7,
        sameCounterparty: true
      }
    },
    
    // 规则类型改变
    onRuleTypeChange() {
      // 可以在这里设置不同规则类型的默认参数
    },
    
    // 构建规则逻辑JSON
    buildRuleLogic() {
      const logic = {
        type: 'SMART_MERGE',
        category: this.ruleForm.category,
        priority: this.ruleForm.priority,

        // 时间条件
        timeRange: this.ruleForm.timeRange,
        customDays: this.ruleForm.customDays,

        // 金额条件
        enableAmountFilter: this.ruleForm.enableAmountFilter,
        minAmount: this.ruleForm.minAmount,
        maxAmount: this.ruleForm.maxAmount,
        amountTolerance: this.ruleForm.amountTolerance,
        tolerancePercent: this.ruleForm.tolerancePercent,

        // 关键词条件
        enableKeywordFilter: this.ruleForm.enableKeywordFilter,
        includeKeywords: this.ruleForm.includeKeywords,
        excludeKeywords: this.ruleForm.excludeKeywords,

        // 人员条件
        enablePersonFilter: this.ruleForm.enablePersonFilter,
        sameSubmitter: this.ruleForm.sameSubmitter,
        sameDepartment: this.ruleForm.sameDepartment,

        // 交易方条件
        enableCounterpartyFilter: this.ruleForm.enableCounterpartyFilter,
        sameCounterparty: this.ruleForm.sameCounterparty,
        specificCounterparty: this.ruleForm.specificCounterparty,

        // 高级设置
        autoExecute: this.ruleForm.autoExecute
      }

      return JSON.stringify(logic)
    },
    
    // 解析规则逻辑
    parseRuleLogic(ruleLogicStr) {
      try {
        const logic = JSON.parse(ruleLogicStr)

        // 新格式规则
        if (logic.type === 'SMART_MERGE') {
          this.ruleForm.category = logic.category || 'CUSTOM'
          this.ruleForm.priority = logic.priority || 'MEDIUM'
          this.ruleForm.timeRange = logic.timeRange || 'WEEK'
          this.ruleForm.customDays = logic.customDays || 7
          this.ruleForm.enableAmountFilter = logic.enableAmountFilter || false
          this.ruleForm.minAmount = logic.minAmount || 0
          this.ruleForm.maxAmount = logic.maxAmount || 10000
          this.ruleForm.amountTolerance = logic.amountTolerance || 0
          this.ruleForm.tolerancePercent = logic.tolerancePercent || 5
          this.ruleForm.enableKeywordFilter = logic.enableKeywordFilter || false
          this.ruleForm.includeKeywords = logic.includeKeywords || ''
          this.ruleForm.excludeKeywords = logic.excludeKeywords || ''
          this.ruleForm.enablePersonFilter = logic.enablePersonFilter || false
          this.ruleForm.sameSubmitter = logic.sameSubmitter || false
          this.ruleForm.sameDepartment = logic.sameDepartment || false
          this.ruleForm.enableCounterpartyFilter = logic.enableCounterpartyFilter || false
          this.ruleForm.sameCounterparty = logic.sameCounterparty || false
          this.ruleForm.specificCounterparty = logic.specificCounterparty || ''
          this.ruleForm.autoExecute = logic.autoExecute || false
        }
        // 兼容旧格式规则
        else {
          this.ruleForm.category = 'CUSTOM'
          this.ruleForm.timeRange = 'WEEK'
          if (logic.conditions) {
            const conditions = logic.conditions
            this.ruleForm.enableAmountFilter = !!(conditions.min_amount || conditions.max_amount)
            this.ruleForm.minAmount = conditions.min_amount || 0
            this.ruleForm.maxAmount = conditions.max_amount || 10000
            this.ruleForm.tolerancePercent = conditions.tolerance_percent || 5
            this.ruleForm.enableCounterpartyFilter = !!conditions.same_counterparty
            this.ruleForm.sameCounterparty = conditions.same_counterparty !== false
          }
        }
      } catch (error) {
        console.error('解析规则逻辑失败:', error)
      }
    },

    // 格式化金额条件
    formatAmountCondition(logic) {
      const parts = []
      if (logic.minAmount || logic.maxAmount) {
        parts.push(`范围: ${logic.minAmount || 0} - ${logic.maxAmount || '∞'}`)
      }
      if (logic.amountTolerance) {
        parts.push(`容差: ${logic.amountTolerance}元`)
      }
      if (logic.tolerancePercent) {
        parts.push(`容差: ${logic.tolerancePercent}%`)
      }
      return parts.join(', ') || '无限制'
    },

    // 格式化关键词条件
    formatKeywordCondition(logic) {
      const parts = []
      if (logic.includeKeywords) {
        parts.push(`包含: ${logic.includeKeywords}`)
      }
      if (logic.excludeKeywords) {
        parts.push(`排除: ${logic.excludeKeywords}`)
      }
      return parts.join('; ') || '无限制'
    },

    // 格式化人员条件
    formatPersonCondition(logic) {
      const parts = []
      if (logic.sameSubmitter) parts.push('相同提交人')
      if (logic.sameDepartment) parts.push('相同部门')
      return parts.join(', ') || '无限制'
    },

    // 格式化交易方条件
    formatCounterpartyCondition(logic) {
      const parts = []
      if (logic.sameCounterparty) parts.push('相同交易方')
      if (logic.specificCounterparty) parts.push(`指定: ${logic.specificCounterparty}`)
      return parts.join(', ') || '无限制'
    },

    // 格式化金额
    formatAmount(amount) {
      return Number(amount).toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    
    // 格式化规则逻辑显示
    formatRuleLogic(ruleLogicStr) {
      try {
        const logic = JSON.parse(ruleLogicStr)
        return JSON.stringify(logic, null, 2)
      } catch (error) {
        return ruleLogicStr
      }
    },
    
    // 获取实体类型标签样式
    getEntityTypeTag(type) {
      const tagMap = {
        'DOCUMENT': 'primary',
        'RECEIPT': 'success',
        'BOTH': 'warning'
      }
      return tagMap[type] || 'info'
    },
    
    // 获取实体类型文本
    getEntityTypeText(type) {
      const textMap = {
        'DOCUMENT': '票据',
        'RECEIPT': '银证',
        'BOTH': '两者'
      }
      return textMap[type] || type
    },
    
    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString()
    }
  }
}
</script>

<style lang="less" scoped>
.merge-rule-management {
  padding: 20px;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.page-header {
  margin-bottom: 30px;
  text-align: center;
}

.page-header h2 {
  margin: 0 0 10px 0;
  color: #2c3e50;
  font-size: 28px;
  font-weight: 600;
}

.page-header p {
  margin: 0;
  color: #7f8c8d;
  font-size: 16px;
}

// 模板卡片样式
.template-section {
  margin-bottom: 30px;
}

.template-section h3 {
  margin-bottom: 20px;
  color: #2c3e50;
  font-size: 20px;
}

.template-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.template-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.template-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  border-color: #3498db;
}

.template-icon {
  font-size: 32px;
  margin-bottom: 10px;
}

.template-content h4 {
  margin: 0 0 8px 0;
  color: #2c3e50;
  font-size: 18px;
}

.template-content p {
  margin: 0 0 12px 0;
  color: #7f8c8d;
  font-size: 14px;
  line-height: 1.4;
}

.template-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.template-tag {
  background: #ecf0f1;
  color: #34495e;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
}

.toolbar {
  margin-bottom: 20px;
}

.toolbar Button {
  margin-right: 10px;
}

// 分类标签
.rule-categories {
  margin-bottom: 20px;
}

.rule-categories Button {
  margin-right: 10px;
  margin-bottom: 10px;
}

// 规则容器
.rules-container {
  display: grid;
  gap: 20px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.empty-state h3 {
  margin: 0 0 10px 0;
  color: #2c3e50;
}

.empty-state p {
  margin: 0;
  color: #7f8c8d;
}

// 规则卡片
.rule-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
}

.rule-card:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
}

.rule-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.rule-title h4 {
  margin: 0 0 8px 0;
  color: #2c3e50;
  font-size: 18px;
  font-weight: 600;
}

.rule-category {
  background: #3498db;
  color: white;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
}

.rule-status {
  display: flex;
  align-items: center;
  gap: 10px;
}

.rule-type {
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  color: white;
  font-weight: 500;
}

.rule-type.document {
  background-color: #3498db;
}

.rule-type.receipt {
  background-color: #27ae60;
}

.rule-type.both {
  background-color: #f39c12;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.active {
  background-color: #d4edda;
  color: #155724;
}

.status-badge.inactive {
  background-color: #f8d7da;
  color: #721c24;
}

.rule-description {
  color: #7f8c8d;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 16px;
}

.rule-conditions {
  margin-bottom: 16px;
}

.condition-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.condition-tag {
  background: #ecf0f1;
  color: #2c3e50;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
}

.rule-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid #ecf0f1;
}

.rule-meta {
  color: #95a5a6;
  font-size: 12px;
}

.rule-meta span {
  margin-right: 16px;
}

.rule-actions Button {
  margin-left: 8px;
}

// 表单样式
.rule-form {
  padding: 0;
}

.form-section {
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.form-section h3 {
  margin: 0 0 20px 0;
  color: #2c3e50;
  font-size: 16px;
  font-weight: 600;
}

.condition-group {
  margin-bottom: 20px;
  padding: 16px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.condition-group h4 {
  margin: 0 0 12px 0;
  color: #495057;
  font-size: 14px;
  font-weight: 600;
}

.form-row {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.form-row label {
  min-width: 120px;
  color: #495057;
  font-weight: 500;
  font-size: 14px;
}

.form-input {
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s ease;
}

.form-input:focus {
  border-color: #3498db;
  outline: none;
}

.form-textarea {
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
  max-width: 500px;
  resize: vertical;
  transition: border-color 0.3s ease;
}

.form-textarea:focus {
  border-color: #3498db;
  outline: none;
}

// 详情对话框样式
.rule-detail {
  padding: 0;
}

.detail-section {
  margin-bottom: 30px;
}

.detail-section h3 {
  margin: 0 0 16px 0;
  color: #2c3e50;
  font-size: 16px;
  font-weight: 600;
  padding-bottom: 8px;
  border-bottom: 2px solid #3498db;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
}

.detail-item {
  display: flex;
  align-items: center;
}

.detail-item label {
  min-width: 100px;
  color: #7f8c8d;
  font-weight: 500;
  margin-right: 12px;
}

.conditions-display {
  display: grid;
  gap: 12px;
}

.condition-item {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 4px solid #3498db;
}

.condition-item strong {
  color: #2c3e50;
  margin-right: 8px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.stat-item {
  text-align: center;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.stat-number {
  font-size: 24px;
  font-weight: 600;
  color: #3498db;
  margin-bottom: 8px;
}

.stat-label {
  color: #7f8c8d;
  font-size: 14px;
}

.rule-logic-display {
  background-color: #2c3e50;
  color: #ecf0f1;
  padding: 16px;
  border-radius: 6px;
  font-size: 12px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
}

// 测试对话框样式
.test-container {
  padding: 0;
}

.test-header {
  margin-bottom: 20px;
  text-align: center;
}

.test-header h3 {
  margin: 0 0 8px 0;
  color: #2c3e50;
}

.test-controls {
  margin-bottom: 20px;
  text-align: center;
}

.test-controls Button {
  margin: 0 10px;
}

.test-results {
  margin-top: 20px;
}

.result-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.summary-item {
  text-align: center;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
}

.summary-number {
  font-size: 20px;
  font-weight: 600;
  color: #3498db;
  display: block;
}

.summary-label {
  color: #7f8c8d;
  font-size: 12px;
}

.test-groups {
  margin-top: 20px;
}

.test-group {
  margin-bottom: 16px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  overflow: hidden;
}

.group-header {
  background: #f8f9fa;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e9ecef;
}

.group-count {
  background: #3498db;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
}

.group-items {
  padding: 12px;
}

.group-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f1f3f4;
}

.group-item:last-child {
  border-bottom: none;
}

.item-name {
  flex: 1;
  color: #2c3e50;
}

.item-amount {
  color: #27ae60;
  font-weight: 500;
  margin: 0 16px;
}

.item-date {
  color: #7f8c8d;
  font-size: 12px;
}

// 导入对话框样式
.import-container {
  padding: 0;
}

.import-tips {
  margin-bottom: 20px;
  padding: 16px;
  background: #e8f4fd;
  border-radius: 6px;
  border-left: 4px solid #3498db;
}

.import-tips h4 {
  margin: 0 0 12px 0;
  color: #2c3e50;
}

.import-tips ul {
  margin: 0;
  padding-left: 20px;
  color: #7f8c8d;
}

.file-upload {
  margin-bottom: 20px;
  text-align: center;
}

.import-preview {
  margin-top: 20px;
}

.preview-rule {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 4px;
  margin-bottom: 8px;
}

// 响应式设计
@media (max-width: 768px) {
  .template-cards {
    grid-template-columns: 1fr;
  }

  .rule-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .rule-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .result-summary {
    grid-template-columns: 1fr;
  }
}
</style>
